import Konva from "konva";
import { KonvaNodeComponent } from "react-konva";

/**
 * Container class extends Konva.Group with optimized getClientRect behavior.
 *
 * This class is used when you need a Group that contains child elements but want to avoid
 * the performance overhead of Konva's default getClientRect implementation, which recursively
 * intersects the rect with all children. Instead, this Container assumes all children are
 * contained within its bounds and returns its own rect directly.
 *
 * This is crucial for performance-sensitive applications where Groups contain many child elements.
 *
 * @example
 * const container = new Container({
 *   x: 0,
 *   y: 0,
 *   width: 100,
 *   height: 100,
 * });
 * container.add(childElement);
 * const rect = container.getClientRect(); // Returns container's rect without recursive child checks
 */
class Container extends Konva.Group {
  constructor(config: Konva.ContainerConfig) {
    if (!config.width || !config.height) {
      throw new Error("Container must have a width and height");
    }

    super(config);
  }

  /**
   * Overrides getClientRect to return the container's own rect without recursively
   * intersecting with children. This provides significant performance improvements
   * when the Group contains many child elements.
   *
   * @param config - Standard Konva GetClientRect options (skipTransform, relativeTo, etc.)
   * @returns The client rect of the container, not intersected with children
   */
  // @ts-expect-error Konva types require full IRect interface, but we return simplified rect
  getClientRect(
    config: {
      skipTransform?: boolean;
      skipShadow?: boolean;
      skipStroke?: boolean;
      relativeTo?: never;
    } = {}
  ): Record<string, unknown> {
    const skipTransform = config.skipTransform;
    const relativeTo: never = config.relativeTo as never;

    // Manually calculate the rect based on configured position and size
    // This avoids the expensive recursive child intersection that Group.getClientRect does
    const bounds = { x: this.x(), y: this.y(), width: this.width(), height: this.height() };

    if (!skipTransform) {
      return this._transformedRect(bounds, relativeTo);
    }
    return bounds;
  }
}

// @ts-expect-error Konva types are mismatched here.
Konva["Container"] = Container;

/**
 * Konva utility for creating optimized Group elements.
 * This class extends the Konva Group class and skips recursive child rect calculations for performance.
 * @returns A new Container instance.
 */
// @ts-expect-error Konva types are mismatched here.
export default "Container" as KonvaNodeComponent<Konva.Group, Konva.ContainerConfig>;
